import axios from 'axios';
import * as Sentry from '@sentry/browser';
import { API_URL, API_KEY, CPE_BASE_URL, CPE_JWT } from '@shared/api/config';
import * as endpoints from '@shared/api/endpoints';
import { sicSaveQueue } from '@shared/utils/requestQueue';
import { UserType } from '@src/types/common';
import PATH_ROUTES from '@src/static/constants/path-routes';
import { refreshUserToken } from '@src/context/user/userUtils';

const APIClient = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    Accept: 'application/json',
    Authorization: API_KEY,
  },
  timeout: 360000,
});

function getCpeJwt() {
  const cognitoIdToken = localStorage.getItem('idToken');

  return cognitoIdToken ? cognitoIdToken : CPE_JWT;
}

export const CPEAPIClient = axios.create({
  baseURL: CPE_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: 'Bearer ' + getCpeJwt(),
  },
  // withCredentials: true,
});

APIClient.interceptors.request.use((config) => {
  const userType = localStorage.getItem('user-type'); // "inspector" | "sic-user"
  const token =
    localStorage.getItem('inspector-cognito-token') ??
    localStorage.getItem('inspector-legacy-token');

  if (
    (userType === UserType.INSPECTOR || userType === UserType.INSPECTOR_SIC) &&
    token
  ) {
    // @ts-expect-error This is a workaround to avoid the type error
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
      'X-User-Type': userType,
    };
  } else {
    // @ts-expect-error This is a workaround to avoid the type error
    config.headers = {
      ...config.headers,
      'X-User-Type': userType,
    };

    if (
      !['/v1/events/save', 'v2/events/save'].includes(config.url ?? '') &&
      localStorage.getItem('accessToken')
    ) {
      config.headers = new axios.AxiosHeaders({
        ...config.headers,
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        'X-User-Type': UserType.SIC_USER,
      });
    }
  }

  return config;
});

// Variable para evitar múltiples refresh simultáneos
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

APIClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    Sentry.captureException(error, (scope) => {
      scope.clear();
      scope.setTag('sic-uri', error?.request?.responseURL ?? 'Uri not found');
      scope.setExtra('error-data', JSON.stringify(error?.response?.data));
      localStorage.setItem('error-data', JSON.stringify(error?.response?.data));

      return scope;
    });

    if (
      ['/v1/events/save'].includes(error.config.url) &&
      (error.message === 'Network Error' ||
        (error.response && error.response.status >= 400))
    ) {
      return new Promise((resolve, reject) => {
        if (error.message === 'Network Error') {
          localStorage.setItem('networkFlag', '1');
        }
        sicSaveQueue.enqueue({
          config: error.config,
          resolve,
          reject,
        });
      });
    } else if (error.response.status === 401) {
      const userType = localStorage.getItem('user-type');

      // Solo intentar refresh para usuarios SIC
      if (userType === UserType.SIC_USER) {
        const originalRequest = error.config;

        if (originalRequest._retry) {
          // Si ya reintentamos y sigue fallando, proceder con la lógica original
          localStorage.removeItem('inspector-cognito-token');
          localStorage.removeItem('inspector-legacy-token');
          localStorage.removeItem('user-type');

          const customEventNotification = new CustomEvent(
            'trigger-expire-session',
            {
              detail: {
                expireSession: true,
              },
            },
          );
          window.dispatchEvent(customEventNotification);
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return APIClient(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshSuccess = await refreshUserToken();
          if (refreshSuccess) {
            const newToken = localStorage.getItem('accessToken');
            processQueue(null, newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return APIClient(originalRequest);
          } else {
            processQueue(error, null);
            // Si el refresh falla, proceder con la lógica original
            localStorage.removeItem('inspector-cognito-token');
            localStorage.removeItem('inspector-legacy-token');
            localStorage.removeItem('user-type');

            const customEventNotification = new CustomEvent(
              'trigger-expire-session',
              {
                detail: {
                  expireSession: true,
                },
              },
            );
            window.dispatchEvent(customEventNotification);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Si hay error en el refresh, proceder con la lógica original
          localStorage.removeItem('inspector-cognito-token');
          localStorage.removeItem('inspector-legacy-token');
          localStorage.removeItem('user-type');

          const customEventNotification = new CustomEvent(
            'trigger-expire-session',
            {
              detail: {
                expireSession: true,
              },
            },
          );
          window.dispatchEvent(customEventNotification);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Lógica original para otros tipos de usuario (inspectores, etc.)
        localStorage.removeItem('inspector-cognito-token');
        localStorage.removeItem('inspector-legacy-token');
        localStorage.removeItem('user-type');

        if (userType === UserType.INSPECTOR) {
          window.location.href = PATH_ROUTES.INSPECTOR_FORM_NOT_AUTHORIZED;
        } else {
          const customEventNotification = new CustomEvent(
            'trigger-expire-session',
            {
              detail: {
                expireSession: true,
              },
            },
          );
          window.dispatchEvent(customEventNotification);
        }
      }
    }

    return Promise.reject(error);
  },
);

export { endpoints };
export default APIClient;
